import { CODE_INSEE_REGEX, CODE_NAF_REGEX, CODE_POSTAL_REGEX, CODE_ROME_REGEX, RNCP_REGEX, SIRET_REGEX, UAI_REGEX } from "../../constants/regex.js"
import { validatePhone } from "../../validators/phone-validator.js"
import { validateSIRET } from "../../validators/siret-validator.js"
import { removeUrlsFromText } from "../common.js"
import { z } from "../zod-with-open-api.js"
import { buildEnum, latitude, longitude } from "./zod-primitives-light.js"

export const createProjectionFromZod = <T extends z.ZodObject<any>>(schema: T, excludeFields: (keyof z.infer<T>)[]): Record<string, 1> => {
  return Object.keys(schema.shape).reduce(
    (acc, key) => {
      if (!excludeFields.includes(key as keyof z.infer<T>)) {
        acc[key] = 1
      }
      return acc
    },
    {} as Record<string, 1>
  )
}

export const extensions = {
  siret: z.string().trim().regex(SIRET_REGEX, "SIRET invalide").refine(validateSIRET, {
    message: "Le siret ne respecte pas l'algorithme luhn (https://fr.wikipedia.org/wiki/Formule_de_Luhn)",
  }),
  uai: () => z.string().trim().regex(UAI_REGEX, "UAI invalide"), // e.g 0123456B
  // z.codec() (not .transform()) so the schema can also be used in response schemas: unlike a
  // bare .transform(), fastify-type-provider-zod@7's encode-based response serialization needs
  // schemas to declare both directions, and a stored value is already sanitized, so encode is a no-op.
  phone: () =>
    z.codec(z.string().trim(), z.string(), {
      decode: (value) => removeUrlsFromText(value),
      encode: (value) => value,
    }), /// is it a phone extensions still ??
  // Same z.codec() rationale as `phone` above: strips URLs from free-text user input
  // (anti-spam) while staying response-schema-safe. Accepts a custom input schema
  // (e.g. z.string().min(1).max(50)) so length/format constraints still apply to
  // the raw input, before sanitization.
  withoutUrls: (inputSchema: z.ZodString = z.string()) =>
    z.codec(inputSchema, z.string(), {
      decode: (value) => removeUrlsFromText(value),
      encode: (value) => value,
    }),
  telephone: z.string().trim().refine(validatePhone, { message: "Invalid Phone Number: please use international format (+XXXX...) or french national format (06XXX...)" }),
  code_naf: () =>
    z.preprocess(
      (v: unknown) => (typeof v === "string" ? v.replace(".", "") : v), // parfois, le code naf contient un point
      z.string().trim().toUpperCase().regex(CODE_NAF_REGEX, "NAF invalide") // e.g 1071D
    ),
  iso8601Date: () =>
    z.preprocess((v: unknown) => (typeof v === "string" && v.match(/^([0-9]{4})-([0-9]{2})-([0-9]{2})/) ? new Date(v.trim()) : v), z.date({ error: "Date invalide" })),
  iso8601Datetime: () =>
    z.preprocess((v: unknown) => (typeof v === "string" && v.match(/^([0-9]{4})-([0-9]{2})-([0-9]{2})/) ? new Date(v.trim()) : v), z.date({ error: "Date invalide" })),
  codeCommuneInsee: () => z.string().regex(/^([0-9]{2}|2A|2B)[0-9]{3}$/, "Format invalide"),
  brevoWebhook: () =>
    z.object({
      event: z.string(),
      email: z.string(),
      id: z.number(),
      ts: z.number(),
      date: z.string().nullish(),
      "message-id": z.string().nullish(),
      ts_event: z.number().nullish(),
      subject: z.string().nullish(),
      "X-Mailin-custom": z.string().nullish(),
      sending_ip: z.string().nullish(),
      ts_epoch: z.number().nullish(),
      template_id: z.number().nullish(),
      tag: z.string().nullish(),
      tags: z.array(z.string()).nullish(),
      link: z.string().nullish(),
      reason: z.string().nullish(),
      date_sent: z.string().nullish(),
      date_event: z.string().nullish(),
      ts_sent: z.number().nullish(),
      camp_id: z.number().nullish(),
      campaign_name: z.string().nullish(),
      URL: z.string().nullish(),
      list_id: z.array(z.number()).nullish(),
      sender_email: z.string().nullish(),
      is_returnpath: z.boolean().nullish(),
      key: z.string().nullish(),
      content: z.array(z.string()).nullish(),
    }),
  // Extraits dans zod-primitives-light.ts (importable côté client sans libphonenumber),
  // ré-exposés ici pour ne pas casser les ~40 importeurs existants de `extensions`.
  buildEnum,
  romeCode: () => z.string().trim().regex(CODE_ROME_REGEX, "Code ROME invalide"),
  romeCodeArray: () =>
    z
      .string()
      .trim()
      .transform((str) => str.split(","))
      .refine((arr) => arr.every((code) => CODE_ROME_REGEX.test(code.trim())), {
        message: "One or more ROME codes are invalid. Expected format is 'D1234'.",
      }),
  rncpCode: () => z.string().trim().regex(RNCP_REGEX, "Code RNCP invalide"),
  latitude,
  longitude,
  inseeCode: () => z.string().trim().regex(CODE_INSEE_REGEX, "Code INSEE invalide"),
  zipCode: () => z.string().trim().regex(CODE_POSTAL_REGEX, "Code postal invalide"),
  url: () => z.string().regex(/^(https?:\/\/)?(http?:\/\/)?(www\.)?([a-zA-Z0-9-]+)(\.[a-zA-Z0-9-]+)+(\.[a-zA-Z]{2,6})(:[0-9]{1,5})?(\/.*)?$/, "URL invalide"),
  optionalToNullish<Schema extends z.ZodObject<any>>(schema: Schema) {
    // cf https://github.com/colinhacks/zod/discussions/2050
    const entries = Object.entries(schema.shape) as [keyof Schema["shape"], z.ZodType][]
    const newProps = entries.reduce(
      (acc, [key, value]) => {
        acc[key] = (value instanceof z.ZodOptional ? (value as z.ZodOptional<z.ZodType>).unwrap().nullish() : value) as any
        return acc
      },
      {} as {
        [key in keyof Schema["shape"]]: Schema["shape"][key] extends z.ZodOptional<infer T> ? z.ZodOptional<z.ZodNullable<T>> : Schema["shape"][key]
      }
    )
    return z.object(newProps)
  },
}
